import React, { useMemo, useState } from 'react';
import { ChatBubbleLeftIcon, ClockIcon, LinkIcon } from '@heroicons/react/24/outline';
import { Event } from '../services/events';
import { formatDate } from '../utils/i18nFormat';

interface TelegramContextViewerProps {
  event: Event;
  pageSize?: number; // default 5
  maxWindow?: number; // default 20
}

type ContextItem = {
  id?: number | string;
  date?: string;
  text?: string;
  author_name?: string;
};

const safeArray = (v: any): ContextItem[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v as ContextItem[];
  return [];
};

const TelegramContextViewer: React.FC<TelegramContextViewerProps> = ({ event, pageSize = 5, maxWindow = 20 }) => {
  const [beforeCount, setBeforeCount] = useState(pageSize);
  const [afterCount, setAfterCount] = useState(pageSize);

  const isTelegram = (event?.source || '').toLowerCase() === 'telegram';
  const metadata = event?.event_metadata || {};

  const channelUsername: string | undefined = metadata.channel_username || undefined;
  const messageText: string | undefined = metadata.text_blob || metadata.message_text || undefined;
  const messageDate: string | undefined = metadata.message_date || event?.occurred_at || event?.created_at;
  const authorName: string | undefined = metadata.author_name || undefined;

  // Parse channel and message id from source_url if available
  const { chan, msgId } = useMemo(() => {
    try {
      const url: string = event?.source_url || '';
      const m = url.match(/https:\/\/t\.me\/(?:s\/)?([^/]+)(?:\/(\d+))?/);
      const channel = m?.[1];
      const id = m?.[2];
      return { chan: channel || channelUsername || undefined, msgId: id ? Number(id) : undefined };
    } catch {
      return { chan: channelUsername || undefined, msgId: undefined };
    }
  }, [event?.source_url, channelUsername]);

  const contextBeforeAll = safeArray(metadata.context_before);
  const contextAfterAll = safeArray(metadata.context_after);

  const contextBefore = useMemo(() => contextBeforeAll.slice(0, Math.min(beforeCount, maxWindow)), [contextBeforeAll, beforeCount, maxWindow]);
  const contextAfter = useMemo(() => contextAfterAll.slice(0, Math.min(afterCount, maxWindow)), [contextAfterAll, afterCount, maxWindow]);

  const canLoadMoreBefore = contextBeforeAll.length > contextBefore.length && contextBefore.length < maxWindow;
  const canLoadMoreAfter = contextAfterAll.length > contextAfter.length && contextAfter.length < maxWindow;

  if (!isTelegram) return null;

  const hasContextData = contextBeforeAll.length > 0 || contextAfterAll.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center">
          <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
          Contexto da mensagem (até 20 antes/depois)
        </h4>
        {/* Link para a conversa removido para evitar exposição do canal */}
      </div>

      {/* Mensagem principal */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
        <div className="flex items-center text-xs text-gray-600 dark:text-gray-300 mb-2">
          {authorName && <span className="mr-2">Autor: {authorName}</span>}
          {messageDate && (
            <span className="flex items-center">
              <ClockIcon className="h-3 w-3 mr-1" /> {formatDate(messageDate)}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
          {messageText || 'Mensagem principal não disponível.'}
        </div>
        {msgId !== undefined && (
          <div className="mt-2 text-xs text-gray-500">ID da mensagem: {msgId}</div>
        )}
      </div>

      {!hasContextData && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded p-3 text-xs text-yellow-800 dark:text-yellow-200">
          Contexto não disponível. Para habilitar, configure sessão de usuário (TELEGRAM_API_ID/TELEGRAM_API_HASH) e use TELEGRAM_MODE=USER no coletor.
        </div>
      )}

      {hasContextData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Antes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Antes</span>
              {canLoadMoreBefore && (
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                  onClick={() => setBeforeCount((c) => Math.min(c + pageSize, maxWindow))}
                >
                  Carregar mais (+{Math.min(pageSize, maxWindow - beforeCount)})
                </button>
              )}
            </div>
            <div className="space-y-2">
              {contextBefore.map((ctx, idx) => (
                <div key={`before-${idx}-${ctx.id ?? idx}`} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    {ctx.author_name && <span className="mr-2">{ctx.author_name}</span>}
                    {ctx.date && <span>{formatDate(ctx.date)}</span>}
                  </div>
                  <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{ctx.text || ''}</div>
                </div>
              ))}
              {contextBefore.length === 0 && <div className="text-xs text-gray-500">Sem mensagens anteriores.</div>}
            </div>
          </div>

          {/* Depois */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Depois</span>
              {canLoadMoreAfter && (
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                  onClick={() => setAfterCount((c) => Math.min(c + pageSize, maxWindow))}
                >
                  Carregar mais (+{Math.min(pageSize, maxWindow - afterCount)})
                </button>
              )}
            </div>
            <div className="space-y-2">
              {contextAfter.map((ctx, idx) => (
                <div key={`after-${idx}-${ctx.id ?? idx}`} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    {ctx.author_name && <span className="mr-2">{ctx.author_name}</span>}
                    {ctx.date && <span>{formatDate(ctx.date)}</span>}
                  </div>
                  <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{ctx.text || ''}</div>
                </div>
              ))}
              {contextAfter.length === 0 && <div className="text-xs text-gray-500">Sem mensagens posteriores.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramContextViewer;